import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  BookOpen,
  Check,
  ChevronDown,
  Copy,
  ExternalLink,
  FileText,
  Info,
  Loader2,
  RefreshCw,
  Search,
} from 'lucide-react';
import * as api from '../../services/api';
import HSCode from '../common/HSCode';


const CATEGORY_ORDER = [
  'Пільги',
  'Заборони',
  'Контроль',
  'Сертифікати',
  'Митне оформлення',
  'Після митного оформлення',
];

const EMPTY_NODES = [];

const CATEGORY_STYLES = {
  benefit: {
    border: 'border-l-emerald-500',
    icon: 'text-emerald-600 dark:text-emerald-400',
    badge: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300',
  },
  restriction: {
    border: 'border-l-rose-500',
    icon: 'text-rose-600 dark:text-rose-400',
    badge: 'bg-rose-50 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300',
  },
  control: {
    border: 'border-l-amber-500',
    icon: 'text-amber-600 dark:text-amber-400',
    badge: 'bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300',
  },
  certificate: {
    border: 'border-l-cyan-500',
    icon: 'text-cyan-600 dark:text-cyan-400',
    badge: 'bg-cyan-50 text-cyan-700 dark:bg-cyan-950/40 dark:text-cyan-300',
  },
  customs: {
    border: 'border-l-blue-500',
    icon: 'text-blue-600 dark:text-blue-400',
    badge: 'bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300',
  },
  technical: {
    border: 'border-l-violet-500',
    icon: 'text-violet-600 dark:text-violet-400',
    badge: 'bg-violet-50 text-violet-700 dark:bg-violet-950/40 dark:text-violet-300',
  },
  payment: {
    border: 'border-l-sky-500',
    icon: 'text-sky-600 dark:text-sky-400',
    badge: 'bg-sky-50 text-sky-700 dark:bg-sky-950/40 dark:text-sky-300',
  },
  conditional: {
    border: 'border-l-slate-400',
    icon: 'text-slate-500 dark:text-slate-400',
    badge: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300',
  },
};


function todayIso() {
  const now = new Date();
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
}


function categoryTone(category = '') {
  if (category.startsWith('Пільги')) return 'benefit';
  if (category.startsWith('Заборони')) return 'restriction';
  if (category.includes('Контроль') || category.includes('подвій')) return 'control';
  if (category.startsWith('Сертифікати')) return 'certificate';
  if (category.startsWith('Митне оформлення')) return 'customs';
  if (category.startsWith('Після митного')) return 'technical';
  return 'conditional';
}


function categoryRank(category) {
  const index = CATEGORY_ORDER.findIndex(prefix => category.startsWith(prefix));
  return index === -1 ? CATEGORY_ORDER.length : index;
}


function htmlToText(value) {
  if (!value) return '';
  if (typeof DOMParser === 'undefined') {
    return String(value).replace(/<br\s*\/?\s*>/gi, '\n').replace(/<[^>]+>/g, ' ');
  }
  const parsed = new DOMParser().parseFromString(String(value).replace(/<br\s*\/?\s*>/gi, '\n'), 'text/html');
  return (parsed.body.textContent || '').replace(/\u00a0/g, ' ').replace(/[ \t]+\n/g, '\n').trim();
}


function Metric({ label, value, tone = 'blue' }) {
  const top = {
    blue: 'border-t-blue-500',
    violet: 'border-t-violet-500',
    amber: 'border-t-amber-500',
    slate: 'border-t-slate-500',
  }[tone];
  return (
    <div className={`rounded-md border border-border-default border-t-2 ${top} bg-elevated px-2.5 py-2`}>
      <div className="text-[9px] font-medium uppercase tracking-wide text-text-muted">{label}</div>
      <div className="mt-0.5 font-mono text-lg font-semibold tabular-nums text-text-primary">{value ?? '—'}</div>
    </div>
  );
}


function LawText({ law }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [content, setContent] = useState('');
  const [error, setError] = useState('');
  const ref = law.text_ref;

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (!next || content || loading || !ref) return;
    setLoading(true);
    setError('');
    try {
      const result = await api.getAnalyticsLawText(ref.text_path, ref.text_file);
      setContent(result.content || 'Текст у базі відсутній.');
    } catch (err) {
      setError(err.message || 'Не вдалося завантажити текст.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="border-t border-border-default first:border-t-0">
      <div className="px-2.5 py-2">
        <div className="flex items-start gap-2">
          <BookOpen className="mt-0.5 h-3.5 w-3.5 shrink-0 text-text-muted" />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-1.5 text-[10px]">
              <span className="font-mono font-semibold text-text-primary">{law.num || 'б/н'}</span>
              {law.date && <span className="text-text-muted">{law.date}</span>}
              <span className="rounded bg-hover px-1 py-0.5 text-[8px] text-text-muted">джерело не звірено</span>
            </div>
            <p className="mt-1 text-[10.5px] leading-snug text-text-secondary">{law.name}</p>
            {ref && (
              <button type="button" onClick={toggle} className="mt-1.5 text-[10px] font-medium text-blue-600 hover:underline dark:text-blue-400">
                {open ? 'Сховати текст' : 'Відкрити текст документа'}
              </button>
            )}
          </div>
        </div>
      </div>
      {open && (
        <div className="max-h-72 overflow-y-auto border-t border-border-default bg-app px-3 py-2.5 text-[10.5px] leading-relaxed text-text-secondary whitespace-pre-wrap">
          {loading && <Loader2 className="h-4 w-4 animate-spin text-blue-500" />}
          {error || content}
        </div>
      )}
    </div>
  );
}


function NodeDetails({ detail, loading, error }) {
  if (loading) {
    return <div className="flex items-center gap-2 px-3 py-4 text-[10px] text-text-muted"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Завантаження умов і джерел…</div>;
  }
  if (error) {
    return <div className="px-3 py-3 text-[10.5px] text-rose-600 dark:text-rose-400">{error}</div>;
  }
  if (!detail) return null;

  const comments = detail.comments || [];
  const laws = detail.law_refs || [];
  const rows = detail.raw_rows || [];

  return (
    <div className="border-t border-border-default bg-app/50">
      {detail.hidden_reason && (
        <div className="m-2 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-2 text-[10px] text-amber-800 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-300">
          Застосування залежить від країни походження та умов угоди.
        </div>
      )}

      {comments.length > 0 && (
        <div className="px-2.5 py-2.5">
          <div className="mb-1.5 text-[9px] font-semibold uppercase tracking-wider text-text-muted">Умови з бази</div>
          <div className="space-y-1.5">
            {comments.map(comment => (
              <div key={comment.recno} className="rounded-md border border-border-default bg-surface px-2.5 py-2 text-[10.5px] leading-relaxed text-text-secondary whitespace-pre-wrap">
                {htmlToText(comment.html || comment.summary || (comment.lines || []).join(' '))}
              </div>
            ))}
          </div>
        </div>
      )}

      {laws.length > 0 && (
        <div className="border-t border-border-default">
          <div className="px-2.5 pt-2.5 text-[9px] font-semibold uppercase tracking-wider text-text-muted">Нормативні підстави</div>
          {laws.map(law => <LawText key={`${law.law_type}:${law.law_code}`} law={law} />)}
        </div>
      )}

      {rows.length > 0 && (
        <details className="border-t border-border-default">
          <summary className="cursor-pointer px-2.5 py-2 text-[10px] font-medium text-text-muted hover:text-text-primary">Діапазони кодів і строки бази · {rows.length}</summary>
          <div className="space-y-1 px-2.5 pb-2.5">
            {rows.map((row, index) => (
              <div key={`${row.hs_code_b}:${row.hs_code_e}:${index}`} className="flex items-start justify-between gap-2 rounded bg-elevated px-2 py-1.5 text-[9.5px]">
                <span className="font-mono text-text-primary">{row.hs_code_b}{row.hs_code_e && row.hs_code_e !== '0' ? ` — ${row.hs_code_e}` : ''}</span>
                <span className="shrink-0 text-text-muted">{row.date_begin || 'без дати'}</span>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}


function NodeRow({ node, code, context, tone }) {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const style = CATEGORY_STYLES[tone] || CATEGORY_STYLES.conditional;

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (!next || detail || loading) return;
    setLoading(true);
    setError('');
    try {
      const result = await api.getAnalyticsNode(code, node.node_id, context);
      setDetail(result.node || null);
    } catch (err) {
      setError(err.message || 'Не вдалося завантажити деталі.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`overflow-hidden rounded-md border border-border-default border-l-2 ${style.border} bg-surface`}>
      <button type="button" onClick={toggle} aria-expanded={open} className="flex w-full items-start gap-2 px-2.5 py-2 text-left hover:bg-hover/70">
        <ChevronDown className={`mt-0.5 h-3.5 w-3.5 shrink-0 text-text-muted transition-transform ${open ? 'rotate-180' : ''}`} />
        <div className="min-w-0 flex-1">
          <div className="text-[10.5px] font-medium leading-snug text-text-primary">{node.title}</div>
          <div className="mt-1 flex flex-wrap items-center gap-1">
            <span className="font-mono text-[8.5px] text-text-disabled">{node.node_id}</span>
            {node.visible === false && <span className={`rounded px-1 py-0.5 text-[8px] font-medium ${CATEGORY_STYLES.conditional.badge}`}>умовно</span>}
          </div>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          {node.rate_summary && <span className={`rounded px-1.5 py-0.5 font-mono text-[9px] font-semibold ${style.badge}`}>{node.rate_summary}</span>}
          <span className="text-[8.5px] text-text-muted">{node.rows_count || 0} ряд.</span>
        </div>
      </button>
      {open && <NodeDetails detail={detail} loading={loading} error={error} />}
    </div>
  );
}


function CategoryBlock({ title, nodes, code, context, tone }) {
  const [open, setOpen] = useState(false);
  const rows = nodes.reduce((sum, node) => sum + (node.rows_count || 0), 0);
  const style = CATEGORY_STYLES[tone] || CATEGORY_STYLES.conditional;

  return (
    <section className={`overflow-hidden rounded-lg border border-border-default border-l-2 ${style.border} bg-elevated`}>
      <button type="button" onClick={() => setOpen(value => !value)} aria-expanded={open} className="flex w-full items-center gap-2 px-3 py-2.5 text-left hover:bg-hover/60">
        <FileText className={`h-3.5 w-3.5 shrink-0 ${style.icon}`} />
        <span className="min-w-0 flex-1 text-[11px] font-semibold leading-snug text-text-primary">{title}</span>
        <span className={`rounded px-1.5 py-0.5 font-mono text-[8.5px] font-semibold ${style.badge}`}>{nodes.length}</span>
        <span className="text-[8.5px] text-text-muted">{rows} ряд.</span>
        <ChevronDown className={`h-3.5 w-3.5 shrink-0 text-text-muted transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="space-y-1.5 border-t border-border-default bg-app/40 p-1.5">
          {nodes.map(node => <NodeRow key={node.node_id} node={node} code={code} context={context} tone={tone} />)}
        </div>
      )}
    </section>
  );
}


function SourceRegister({ sources }) {
  if (!sources?.length) return null;
  return (
    <details className="rounded-lg border border-border-default bg-elevated">
      <summary className="flex cursor-pointer items-center gap-2 px-3 py-2.5 text-[10.5px] font-medium text-text-secondary">
        <BookOpen className="h-3.5 w-3.5 text-text-muted" />
        Нормативні джерела
        <span className="ml-auto font-mono text-[9px] text-text-muted">{sources.length}</span>
      </summary>
      <div className="max-h-72 overflow-y-auto border-t border-border-default divide-y divide-border-default">
        {sources.map(source => (
          <div key={`${source.law_type}:${source.law_code}`} className="px-3 py-2">
            <div className="flex items-center gap-1.5 text-[9.5px]">
              <span className="font-mono font-semibold text-text-primary">{source.num || 'б/н'}</span>
              <span className="text-text-muted">{source.date}</span>
            </div>
            <div className="mt-0.5 text-[10px] leading-snug text-text-secondary">{source.name}</div>
          </div>
        ))}
      </div>
    </details>
  );
}


export default function AnalyticsCardPanel({ code, onCountChange }) {
  const initialDate = useMemo(todayIso, []);
  const [draftDate, setDraftDate] = useState(initialDate);
  const [draftOrigin, setDraftOrigin] = useState('');
  const [context, setContext] = useState({ date: initialDate, ccdType: 101, originCountry: null });
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [copied, setCopied] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  const normalizedCode = String(code || '').replace(/\s+/g, '');
  const codeSupported = /^\d{4,10}$/.test(normalizedCode);

  useEffect(() => {
    if (!codeSupported) {
      setCard(null);
      setError('Оберіть цифровий код УКТЗЕД щонайменше з 4 знаків.');
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError('');
    api.getAnalyticsCard(normalizedCode, context)
      .then(result => {
        if (cancelled) return;
        setCard(result);
        onCountChange?.((result.non_tariff || []).length);
      })
      .catch(err => {
        if (!cancelled) setError(err.message || 'Не вдалося сформувати довідку.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [normalizedCode, codeSupported, context, reloadToken, onCountChange]);

  const resolvedCode = card?.header?.code || normalizedCode;
  const payments = card?.payments || EMPTY_NODES;
  const measures = card?.non_tariff || EMPTY_NODES;
  const hidden = card?.hidden || EMPTY_NODES;
  const duty = payments.find(node => node.node_id === 'payment:10')?.rate_summary;
  const vat = payments.find(node => node.node_id === 'payment:30')?.rate_summary;

  const groups = useMemo(() => {
    if (!card?.header) return [];
    const q = search.trim().toLocaleLowerCase('uk');
    const match = node => !q || [
      node.title,
      node.title_ru,
      node.category,
      node.node_id,
      ...(node.law_refs || []).flatMap(law => [law.num, law.name]),
    ].filter(Boolean).join(' ').toLocaleLowerCase('uk').includes(q);

    const result = [];
    const visiblePayments = payments.filter(match);
    if (visiblePayments.length) {
      result.push({ key: 'payments', title: 'Платежі при імпорті', tone: 'payment', nodes: visiblePayments });
    }

    const byCategory = new Map();
    measures.filter(match).forEach(node => {
      const category = node.category || 'Інші заходи';
      if (!byCategory.has(category)) byCategory.set(category, []);
      byCategory.get(category).push(node);
    });
    [...byCategory.entries()]
      .sort(([a], [b]) => categoryRank(a) - categoryRank(b) || a.localeCompare(b, 'uk'))
      .forEach(([title, nodes]) => result.push({ key: title, title, tone: categoryTone(title), nodes }));

    const visibleHidden = hidden.filter(match);
    if (visibleHidden.length) {
      result.push({ key: 'conditional', title: 'Умовні угоди та преференції', tone: 'conditional', nodes: visibleHidden });
    }
    return result;
  }, [card, payments, measures, hidden, search]);

  const applyContext = event => {
    event.preventDefault();
    const parsedOrigin = draftOrigin ? Number.parseInt(draftOrigin, 10) : null;
    setContext({
      date: draftDate || initialDate,
      ccdType: 101,
      originCountry: Number.isFinite(parsedOrigin) ? parsedOrigin : null,
    });
  };

  const copySummary = async () => {
    if (!card?.header || !navigator.clipboard) return;
    const lines = [
      `${card.header.code} — ${card.header.name || ''}`,
      `Дата: ${context.date}; режим: IM40`,
      `Мито: ${duty || '—'}; ПДВ: ${vat || '—'}`,
      `Нетарифні групи: ${measures.length}; умовні: ${hidden.length}`,
      ...measures.map(node => `• ${node.title} (${node.rows_count || 0})`),
    ];
    await navigator.clipboard.writeText(lines.join('\n'));
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  };

  const openFullReport = () => {
    const url = api.getAnalyticsReportUrl(resolvedCode, context);
    const opened = window.open(url, '_blank', 'noopener,noreferrer');
    if (opened) opened.opener = null;
  };

  if (!codeSupported && !loading) {
    return (
      <div className="flex flex-1 items-center justify-center px-5 text-center">
        <div>
          <Info className="mx-auto h-5 w-5 text-text-muted" />
          <p className="mt-2 text-[11px] text-text-muted">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex-shrink-0 border-b border-border-default px-3 py-2.5">
        <form onSubmit={applyContext} className="grid grid-cols-[minmax(120px,1fr)_100px_30px] gap-1.5">
          <label className="min-w-0">
            <span className="sr-only">Дата довідки</span>
            <input type="date" value={draftDate} onChange={event => setDraftDate(event.target.value)} className="h-8 w-full rounded-md border border-border-default bg-app px-2 text-[10px] text-text-primary focus:border-ring-focus focus:outline-none" />
          </label>
          <label className="min-w-0">
            <span className="sr-only">Код країни походження</span>
            <input type="number" min="1" max="999" value={draftOrigin} onChange={event => setDraftOrigin(event.target.value)} placeholder="ISO країни" title="Числовий ISO-код країни походження" className="h-8 w-full rounded-md border border-border-default bg-app px-2 text-[10px] text-text-primary placeholder:text-text-disabled focus:border-ring-focus focus:outline-none" />
          </label>
          <button type="submit" className="grid h-8 w-8 place-items-center rounded-md border border-border-default bg-elevated text-text-muted hover:border-blue-400 hover:text-blue-500" title="Застосувати контекст">
            <RefreshCw className="h-3.5 w-3.5" />
          </button>
        </form>
        <div className="mt-1.5 flex items-center gap-1.5 text-[9px] text-text-muted">
          <span className="rounded bg-hover px-1.5 py-0.5">Імпорт · IM40</span>
          <span className="rounded bg-hover px-1.5 py-0.5">тип 101</span>
          <span className="truncate">{context.originCountry ? `походження ${context.originCountry}` : 'походження не вказано'}</span>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-3 py-3 scrollbar-thin">
        {loading && !card && (
          <div className="space-y-2">
            <div className="h-14 animate-pulse rounded-lg bg-hover" />
            <div className="grid grid-cols-2 gap-1.5"><div className="h-14 animate-pulse rounded bg-hover" /><div className="h-14 animate-pulse rounded bg-hover" /></div>
            {[1, 2, 3, 4].map(item => <div key={item} className="h-10 animate-pulse rounded bg-hover" />)}
          </div>
        )}

        {error && (
          <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-[10.5px] text-rose-700 dark:border-rose-900/50 dark:bg-rose-950/30 dark:text-rose-300">
            <div className="flex items-start gap-2"><AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" /><span>{error}</span></div>
            <button type="button" onClick={() => setReloadToken(value => value + 1)} className="mt-2 font-medium underline">Повторити</button>
          </div>
        )}

        {card?.header && (
          <div className="space-y-2.5">
            <section className="rounded-lg border border-border-default bg-elevated p-3">
              <div className="flex items-start gap-2">
                <HSCode code={card.header.code} size="sm" />
                <span className="min-w-0 flex-1 text-[11px] font-semibold leading-snug text-text-primary">{card.header.name}</span>
              </div>
              {card.resolved_from && (
                <div className="mt-2 rounded-md border border-blue-200 bg-blue-50 px-2 py-1.5 text-[9.5px] text-blue-700 dark:border-blue-900/50 dark:bg-blue-950/30 dark:text-blue-300">
                  Префікс {card.resolved_from} автоматично уточнено до повного коду.
                </div>
              )}
            </section>

            <div className="grid grid-cols-2 gap-1.5">
              <Metric label="Базове мито" value={duty} tone="blue" />
              <Metric label="ПДВ" value={vat} tone="blue" />
              <Metric label="Нетарифні групи" value={measures.length} tone="violet" />
              <Metric label="Умовні заходи" value={hidden.length} tone="slate" />
            </div>

            <div className="rounded-md border border-amber-200 bg-amber-50/80 px-2.5 py-2 text-[9.5px] leading-relaxed text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/25 dark:text-amber-200">
              <Info className="mr-1 inline h-3 w-3" />
              Це потенційно застосовні заходи. Остаточна вимога залежить від товару, призначення, походження та умов операції.
            </div>

            <label className="relative block">
              <Search className="absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2 text-text-disabled" />
              <input type="search" value={search} onChange={event => setSearch(event.target.value)} placeholder="Пошук у довідці…" className="h-8 w-full rounded-md border border-border-default bg-app pl-7 pr-2 text-[10px] text-text-primary placeholder:text-text-disabled focus:border-ring-focus focus:outline-none" />
            </label>

            <div>
              <div className="mb-1.5 text-[9px] font-semibold uppercase tracking-wider text-text-muted">Що потрібно перевірити</div>
              <div className="space-y-1.5">
                {groups.map(({ key, ...group }) => (
                  <CategoryBlock key={key} {...group} code={resolvedCode} context={context} />
                ))}
                {groups.length === 0 && <div className="py-4 text-center text-[10px] text-text-muted">За цим пошуком нічого не знайдено.</div>}
              </div>
            </div>

            <SourceRegister sources={card.sources} />

            <div className="rounded-md border border-border-default bg-app px-2.5 py-2 text-[9px] leading-relaxed text-text-muted">
              MasterD/DeclPlus — прикладний індекс. Реквізити та актуальність потрібно звіряти з офіційними джерелами.
            </div>
          </div>
        )}
      </div>

      {card?.header && (
        <div className="flex flex-shrink-0 items-center gap-1.5 border-t border-border-default px-3 py-2">
          <button type="button" onClick={copySummary} className="inline-flex h-8 flex-1 items-center justify-center gap-1.5 rounded-md border border-border-default bg-elevated text-[10px] font-medium text-text-secondary hover:bg-hover">
            {copied ? <Check className="h-3.5 w-3.5 text-emerald-500" /> : <Copy className="h-3.5 w-3.5" />}
            {copied ? 'Скопійовано' : 'Копіювати'}
          </button>
          <button type="button" onClick={openFullReport} className="inline-flex h-8 flex-1 items-center justify-center gap-1.5 rounded-md bg-blue-600 text-[10px] font-medium text-white hover:bg-blue-700">
            <ExternalLink className="h-3.5 w-3.5" />
            Повна версія
          </button>
        </div>
      )}
    </div>
  );
}
